import React, { Fragment, useState, useEffect } from 'react';
import Header from '../../general/navigation/Header';
import Select from 'react-select';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  fetchCountriesWithExistingVisas,
  fetchCompaniesForCountry,
  fetchExistingVisasForCountryAndCompany,
  fetchVisaTree,
} from '../../services/ExistingVisaTreeService';
import Loading from '../../general/components/Loading';
import TreeViewer from './TreeViewer/TreeViewer.js';
import './ExistingVisaTree.scss';

const ExistingVisaTree = () => {
  // Loading states
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
  const [isLoadingVisas, setIsLoadingVisas] = useState(false);
  const [isLoadingTree, setIsLoadingTree] = useState(false);

  // Data states
  const [countries, setCountries] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [visas, setVisas] = useState([]);
  const [treeData, setTreeData] = useState(null);

  // Selection states
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedVisa, setSelectedVisa] = useState(null);

  // Viewer state
  const [showViewer, setShowViewer] = useState(false);

  // Fetch countries on mount
  useEffect(() => {
    const loadCountries = async () => {
      try {
        const data = await fetchCountriesWithExistingVisas();
        setCountries(data);
      } catch (error) {
        console.error('Error fetching countries:', error);
        toast.error('Failed to load countries');
      } finally {
        setIsLoading(false);
      }
    };
    loadCountries();
  }, []);

  // Fetch companies when country changes
  useEffect(() => {
    if (!selectedCountry) {
      setCompanies([]);
      setSelectedCompany(null);
      return;
    }

    const loadCompanies = async () => {
      setIsLoadingCompanies(true);
      setSelectedCompany(null);
      setSelectedVisa(null);
      setVisas([]);
      setTreeData(null);
      
      try {
        const data = await fetchCompaniesForCountry(selectedCountry.value.id);
        setCompanies(data);
      } catch (error) {
        console.error('Error fetching companies:', error);
        toast.error('Failed to load companies');
      } finally {
        setIsLoadingCompanies(false);
      }
    };
    loadCompanies();
  }, [selectedCountry]);

  // Fetch visas when company changes
  useEffect(() => {
    if (!selectedCountry || !selectedCompany) {
      setVisas([]);
      setSelectedVisa(null);
      setTreeData(null);
      return;
    }

    const loadVisas = async () => {
      setIsLoadingVisas(true);
      setSelectedVisa(null);
      setTreeData(null);
      
      try {
        const data = await fetchExistingVisasForCountryAndCompany(
          selectedCountry.value.id,
          selectedCompany.value.id
        );
        setVisas(data);
      } catch (error) {
        console.error('Error fetching visas:', error);
        toast.error('Failed to load existing visas');
      } finally {
        setIsLoadingVisas(false);
      }
    };
    loadVisas();
  }, [selectedCountry, selectedCompany]);

  // Format options for react-select
  const countryOptions = countries.map(country => ({
    value: country,
    label: country.name,
  }));

  const companyOptions = companies.map(company => ({
    value: company,
    label: company.name.replace(/\s*\([^)]*\)/g, '').trim(),
  }));

  const visaOptions = visas.map(visa => ({
    value: visa,
    label: visa.name.replace(/\s*\([^)]*\)/g, '').trim(),
  }));

  // Handle country selection
  const handleSelectCountry = (option) => {
    setSelectedCountry(option);
  };

  // Handle company selection
  const handleSelectCompany = (option) => {
    setSelectedCompany(option);
  };

  // Handle visa selection
  const handleSelectVisa = (option) => {
    setSelectedVisa(option);
  };

  // View tree handler
  const handleViewTree = async () => {
    if (!selectedVisa) return;

    setIsLoadingTree(true);
    try {
      const data = await fetchVisaTree(selectedVisa.value.id);
      if (data && data.root_node) {
        setTreeData(data);
        setShowViewer(true);
      } else {
        toast.warning('This visa has no decision tree configured');
      }
    } catch (error) {
      console.error('Error fetching tree:', error);
      toast.error('Failed to load decision tree');
    } finally {
      setIsLoadingTree(false);
    }
  };

  // Close viewer handler
  const handleCloseViewer = () => {
    toast.dismiss();
    setShowViewer(false);
  };

  if (isLoading) {
    return <Loading />;
  }

  // Show tree viewer when active
  if (showViewer && treeData) {
    return (
      <TreeViewer
        treeData={treeData}
        visaName={selectedVisa.value.name}
        visaId={selectedVisa.value.id}
        companyId={selectedCompany?.value?.id || null}
        onClose={handleCloseViewer}
        editMode={false}
      />
    );
  }

  return (
    <Fragment>
      <Header 
        showClientNavigation={false} 
        showImmigrationNavigation={false} 
        displayName="Existing Visa Trees" 
        link="/home"
      />
      <ToastContainer 
        autoClose={4000} 
        position="top-right"
        hideProgressBar={false}
        closeOnClick
        pauseOnHover={false}
        theme="light"
      />
      
      <div className="existing-visa-tree-container">
        <div className="existing-visa-tree-header">
          <h2>Existing Visa Tree Viewer</h2>
          <p className="existing-visa-tree-subtitle">
            Select a country, company, and visa to view the decision tree visualization
          </p>
        </div>

        <div className="existing-visa-tree-form">
          {/* Country Dropdown */}
          <div className="form-group">
            <label className="form-label">Country</label>
            <Select
              className="existing-visa-select"
              classNamePrefix="react-select"
              placeholder="Select a country..."
              options={countryOptions}
              value={selectedCountry}
              onChange={handleSelectCountry}
              isClearable
              isSearchable
            />
          </div>

          {/* Company Dropdown */}
          <div className="form-group">
            <label className="form-label">Company</label>
            <Select
              className="existing-visa-select"
              classNamePrefix="react-select"
              placeholder={selectedCountry ? "Select a company..." : "Select a country first..."}
              options={companyOptions}
              value={selectedCompany}
              onChange={handleSelectCompany}
              isDisabled={!selectedCountry}
              isLoading={isLoadingCompanies}
              isClearable
              isSearchable
            />
          </div>

          {/* Visa Dropdown */}
          <div className="form-group">
            <label className="form-label">Existing Visa</label>
            <Select
              className="existing-visa-select"
              classNamePrefix="react-select"
              placeholder={selectedCompany ? "Select an existing visa..." : "Select a company first..."}
              options={visaOptions}
              value={selectedVisa}
              onChange={handleSelectVisa}
              isDisabled={!selectedCompany}
              isLoading={isLoadingVisas}
              isClearable
              isSearchable
            />
          </div>

          {/* Action Button */}
          <div className="form-group">
            <button
              className={`view-btn ${!selectedVisa || isLoadingTree ? 'disabled' : ''}`}
              onClick={handleViewTree}
              disabled={!selectedVisa || isLoadingTree}
            >
              {isLoadingTree ? (
                <>
                  <span className="spinner"></span>
                  Loading Tree...
                </>
              ) : (
                <>
                  <svg 
                    className="btn-icon" 
                    width="20" 
                    height="20" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2"
                  >
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                  View Decision Tree
                </>
              )}
            </button>
          </div>

          {/* Selected visa info */}
          {selectedVisa && (
            <div className="selected-visa-info">
              <div className="info-card">
                <div className="info-header">
                  <svg className="info-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="16" x2="12" y2="12"/>
                    <line x1="12" y1="8" x2="12.01" y2="8"/>
                  </svg>
                  <span className="info-label">Selected Visa</span>
                </div>
                <div className="info-value">{selectedVisa.value.name}</div>
                <div className="info-meta">
                  <span className="info-badge">ID: {selectedVisa.value.id}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

    </Fragment>
  );
};

export default ExistingVisaTree;
